import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

/**
 * IrrigationFertilizationWidget
 *
 * Combined irrigation + fertilization summary: system stats, monthly water usage trend,
 * fertilization cycle history, and annual nutrient totals.
 */

const MiniTrend = ({ data, color = "#059669", height = 44 }) => {
  const [ref, inView] = useInView({ triggerOnce: true });
  if (!data || data.length < 2) return null;

  const max = Math.max(...data.map((d) => d.value));
  const min = Math.min(...data.map((d) => d.value));
  const range = max - min || 1;
  const w = 220;

  const points = data.map((d, i) => ({
    x: (i / (data.length - 1)) * w,
    y: height - ((d.value - min) / range) * (height - 8) - 4,
  }));

  const d = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaD = `${d} L ${w} ${height} L 0 ${height} Z`;

  return (
    <svg ref={ref} viewBox={`0 0 ${w} ${height}`} className="w-full" preserveAspectRatio="xMidYMid meet">
      <path d={areaD} fill={color} opacity={0.06} />
      {inView && (
        <motion.path
          d={d}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1, ease: "easeInOut" }}
        />
      )}
    </svg>
  );
};

const methodIcon = (method) => {
  const m = method.toLowerCase();
  if (m.includes("drip") || m.includes("fertigation")) return "💧";
  if (m.includes("broadcast") || m.includes("incorporation")) return "🌿";
  if (m.includes("foliar") || m.includes("spray")) return "🍃";
  if (m.includes("injection")) return "💉";
  if (m.includes("plow") || m.includes("manure")) return "🔄";
  if (m.includes("canal") || m.includes("lake")) return "🏞️";
  return "🧪";
};

const IrrigationFertilizationWidget = ({ irrigation, fertilization, isArabic }) => {
  const [ref, inView] = useInView({ triggerOnce: true, rootMargin: "-30px" });

  if (!irrigation && !fertilization) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden h-full">
      {/* Header */}
      <div className={`px-5 py-4 border-b border-slate-100 flex items-center gap-3 ${isArabic ? "flex-row-reverse" : ""}`}>
        <div className="w-9 h-9 rounded-xl bg-cyan-50 flex items-center justify-center text-lg flex-shrink-0">💧</div>
        <div className={isArabic ? "text-right" : ""}>
          <h3 className="text-sm font-bold text-slate-900 mb-0">
            {isArabic ? "الري والتسميد" : "Irrigation & Fertilization"}
          </h3>
          <p className="text-[11px] text-slate-400 mb-0">
            {isArabic ? "ملخص الدورات والاستهلاك" : "Cycles, usage & nutrient totals"}
          </p>
        </div>
      </div>

      <div ref={ref} className="p-5 space-y-5">
        {/* Irrigation stats row */}
        {irrigation && (
          <div>
            <p className={`text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2.5 ${isArabic ? "text-right" : ""}`}>
              {isArabic ? "نظام الري" : "Irrigation System"}
            </p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: isArabic ? "كفاءة المياه" : "WUE", value: `${irrigation.wue}x`, color: "text-emerald-700" },
                { label: isArabic ? "توفير المياه" : "Water Saved", value: irrigation.waterSaved, color: "text-blue-700" },
                { label: isArabic ? "الشبكة" : "Network", value: `${irrigation.networkKm} km`, color: "text-slate-700" },
              ].map((s) => (
                <div key={s.label} className="rounded-lg bg-slate-50 border border-slate-100 p-2 text-center">
                  <p className={`text-base font-bold ${s.color} mb-0`}>{s.value}</p>
                  <p className="text-[8px] font-bold uppercase tracking-widest text-slate-400 mb-0">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Water usage trend */}
            {irrigation.monthlyUsage && irrigation.monthlyUsage.length > 0 && (
              <div className="mt-3">
                <p className={`text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1 ${isArabic ? "text-right" : ""}`}>
                  {isArabic ? "استهلاك المياه الشهري (م³)" : "Monthly Water Usage (m³)"}
                </p>
                <MiniTrend data={irrigation.monthlyUsage} color="#0ea5e9" />
              </div>
            )}

            {/* System info badges */}
            <div className={`flex flex-wrap gap-1.5 mt-2 ${isArabic ? "flex-row-reverse" : ""}`}>
              {[
                { label: irrigation.systemType },
                { label: irrigation.pumpSource },
                { label: `${irrigation.avgConsumption} m³/ac/mo` },
              ].filter(Boolean).map((b) => (
                <span key={b.label} className="text-[9px] font-medium text-cyan-800 bg-cyan-50 border border-cyan-100 px-2 py-0.5 rounded-full">
                  {b.label}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Divider */}
        {irrigation && fertilization && (
          <div className="border-t border-dashed border-slate-200" />
        )}

        {/* Fertilization section */}
        {fertilization && (
          <div>
            <div className={`flex items-center justify-between mb-2.5 ${isArabic ? "flex-row-reverse" : ""}`}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-0">
                {isArabic ? "التسميد" : "Fertilization"}
              </p>
              <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                {fertilization.cyclesCompleted} {isArabic ? "دورات مكتملة" : "cycles completed"}
              </span>
            </div>

            {/* Current cycle + total applications */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="rounded-lg bg-green-50 border border-green-100 p-2.5">
                <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">
                  {isArabic ? "الدورة الحالية" : "Current Cycle"}
                </p>
                <p className="text-xs font-bold text-green-800 mb-0">{fertilization.currentCycle}</p>
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-100 p-2.5">
                <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">
                  {isArabic ? "إجمالي التطبيقات" : "Total Applications"}
                </p>
                <p className="text-xs font-bold text-slate-800 mb-0">{fertilization.totalApplications}</p>
              </div>
            </div>

            {/* Nutrient totals */}
            {fertilization.annualTotals && (
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[
                  { label: "N", value: fertilization.annualTotals.nitrogen, color: "bg-green-50 border-green-100 text-green-800" },
                  { label: "P", value: fertilization.annualTotals.phosphorus, color: "bg-orange-50 border-orange-100 text-orange-800" },
                  { label: "K", value: fertilization.annualTotals.potassium, color: "bg-purple-50 border-purple-100 text-purple-800" },
                ].map((n) => (
                  <div key={n.label} className={`rounded-lg border p-2 text-center ${n.color}`}>
                    <p className="text-[10px] font-bold mb-0">{n.label}</p>
                    <p className="text-[10px] font-bold mb-0">{n.value}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Recent application history */}
            {fertilization.history && fertilization.history.length > 0 && (
              <div>
                <p className={`text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-2 ${isArabic ? "text-right" : ""}`}>
                  {isArabic ? "آخر التطبيقات" : "Recent Applications"}
                </p>
                <div className="space-y-1.5 max-h-36 overflow-y-auto">
                  {fertilization.history.slice(0, 4).map((entry, i) => (
                    <motion.div
                      key={`${entry.date}-${entry.type}`}
                      className={`flex items-center gap-2.5 rounded-lg bg-slate-50 border border-slate-100 p-2 ${isArabic ? "flex-row-reverse" : ""}`}
                      initial={{ opacity: 0, y: 6 }}
                      animate={inView ? { opacity: 1, y: 0 } : {}}
                      transition={{ delay: i * 0.08, duration: 0.3 }}
                    >
                      <span className="text-sm flex-shrink-0">{methodIcon(entry.method)}</span>
                      <div className={`flex-1 min-w-0 ${isArabic ? "text-right" : ""}`}>
                        <p className="text-[10px] font-bold text-slate-700 mb-0 truncate">{entry.type}</p>
                        <p className="text-[9px] text-slate-400 mb-0">{entry.amount} — {entry.method}</p>
                      </div>
                      <span className="text-[9px] font-medium text-slate-400 flex-shrink-0">{entry.date}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Methods badges */}
            {fertilization.methods && fertilization.methods.length > 0 && (
              <div className={`flex flex-wrap gap-1.5 mt-2 ${isArabic ? "flex-row-reverse" : ""}`}>
                {fertilization.methods.map((m) => (
                  <span key={m} className="text-[9px] font-medium text-green-800 bg-green-50 border border-green-100 px-2 py-0.5 rounded-full">
                    {methodIcon(m)} {m}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IrrigationFertilizationWidget;
